import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import css from "./OrdersReplaceDishOrderModal.module.css";
import { availableDishesActions } from "../../../../store/slices/menu-slices";
import { ordersService } from "../../../../services/orders-services";
import { ordersActions } from "../../../../store/slices/orders-slices";
import { ImageReusable, SearchableSelect } from "../../../../ui";
import { DishDetailsPopup } from "../DishDetailsPopup/DishDetailsPopup";
import { groupDishesByParentChild } from "../../../../helpers/groupDishesByParentChild";
import { DishType } from "../../../../constants/enums";
import { dishesService } from "../../../../services/dishes-services";
import { cycleAvailableDishesService } from "../../../../services/menu-services/cycleAvailableDishes.service";

function getCycleDay(date, startDate, lengthDays) {
  const diff = dayjs(date).diff(dayjs(startDate), "day");
  return ((diff % lengthDays) + lengthDays) % lengthDays + 1;
}

const OrdersReplaceDishOrderModal = ({
  setIsShowReplace,
  date,
  idForUpdateDishInOrderClient,
  currentDish,
  replacementType = "base",
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { register, handleSubmit, setValue } = useForm();
  const currentLanguage = i18n.language;

  // State management
  const [selectedDish, setSelectedDish] = useState("");
  const [availableDishes, setAvailableDishes] = useState([]);
  const [cycleAllDishes, setCycleAllDishes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDishes, setIsFetchingDishes] = useState(true);
  const [isDishDetailsOpen, setIsDishDetailsOpen] = useState(false);
  const [selectedDishData, setSelectedDishData] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [subDishSearch, setSubDishSearch] = useState("");

  const { menuFormat, cycleConfig } = useSelector((store) => store.appSettings);
  const isCycleMode = menuFormat === "period_based";

  let { availableDishesArray, trigger, availableDishesArrayFiltered, status } =
    useSelector((store) => store.availableDishes);

  // Grouping logic imported from shared helper

  // Clear search when component mounts and unmounts
  useEffect(() => {
    dispatch(availableDishesActions.clearSearch());
    setValue("searchInput", "");

    return () => {
      dispatch(availableDishesActions.clearSearch());
    };
  }, [dispatch, setValue]);

  // Cycle mode: fetch once and filter client-side for both base and protein/sauce
  useEffect(() => {
    if (!isCycleMode) return;
    const fetchCycleDishes = async () => {
      try {
        setIsFetchingDishes(true);
        const cycleDay = cycleConfig?.startDate
          ? getCycleDay(date, cycleConfig.startDate, cycleConfig.lengthDays || 7)
          : 1;
        const items = await cycleAvailableDishesService.getByDay(cycleDay);
        let dishes = (items || []).map((item) => item.dish).filter(Boolean);

        if (replacementType === "protein") {
          dishes = dishes.filter((d) => d.type === DishType.PROTEIN);
          const mapped = dishes.map((d) => ({ id: d.id, dishId: d.id, dish: d, childDishes: [] }));
          setCycleAllDishes(mapped);
          setAvailableDishes(mapped);
        } else if (replacementType === "sauce") {
          dishes = dishes.filter((d) => d.type === DishType.SAUCE);
          const mapped = dishes.map((d) => ({ id: d.id, dishId: d.id, dish: d, childDishes: [] }));
          setCycleAllDishes(mapped);
          setAvailableDishes(mapped);
        } else {
          // base: exclude protein/sauce, group by parent/child
          dishes = dishes.filter((d) => d.type !== DishType.PROTEIN && d.type !== DishType.SAUCE);
          const mapped = dishes.map((d) => ({ id: d.id, dishId: d.id, dish: d, childDishes: [] }));
          const grouped = groupDishesByParentChild(mapped);
          setCycleAllDishes(grouped);
          setAvailableDishes(grouped);
        }
      } catch (e) {
        console.error("Error fetching cycle available dishes:", e);
        toast.error(t("modal-toast-fetch-error"));
      } finally {
        setIsFetchingDishes(false);
      }
    };
    fetchCycleDishes();
  }, [isCycleMode, date, replacementType, cycleConfig, t]);

  // Date mode: fetch base dishes via Redux available-dishes
  useEffect(() => {
    if (isCycleMode || replacementType !== "base") return;
    const fetchAvailableDishes = async () => {
      try {
        setIsFetchingDishes(true);
        if (date) {
          dispatch(availableDishesActions.getAllAvailableDishes({ currentData: date }));
        }
      } catch (e) {
        console.error("Error fetching available dishes:", e);
        toast.error(t("modal-toast-fetch-error"));
      } finally {
        setIsFetchingDishes(false);
      }
    };
    fetchAvailableDishes();
  }, [trigger, date, dispatch, t, isCycleMode, replacementType]);

  // Date mode: fetch protein/sauce from paginated endpoint
  useEffect(() => {
    if (isCycleMode || replacementType === "base") return;
    const fetchSubDishes = async () => {
      setIsFetchingDishes(true);
      try {
        const data = await dishesService.getAllDishesPaginated({
          page: 1,
          word: subDishSearch,
          type: replacementType === "protein" ? DishType.PROTEIN : DishType.SAUCE,
        });
        const list = data.data ?? data;
        setAvailableDishes(
          list.map((dish) => ({ id: dish.id, dishId: dish.id, dish, childDishes: [] }))
        );
      } catch {
        toast.error(t("modal-toast-fetch-error"));
      } finally {
        setIsFetchingDishes(false);
      }
    };
    fetchSubDishes();
  }, [replacementType, subDishSearch, t, isCycleMode]);

  // Date mode: process base dishes from Redux store
  useEffect(() => {
    if (isCycleMode || replacementType !== "base") return;
    if (availableDishesArray && availableDishesArray.length > 0) {
      const dishesToProcess =
        availableDishesArrayFiltered.length > 0
          ? availableDishesArrayFiltered
          : availableDishesArray;
      const dishesToGroup = dishesToProcess.filter(
        (d) => d.dish?.type !== DishType.PROTEIN && d.dish?.type !== DishType.SAUCE
      );
      setAvailableDishes(groupDishesByParentChild(dishesToGroup));
      setIsFetchingDishes(false);
    }
  }, [availableDishesArray, availableDishesArrayFiltered, replacementType, isCycleMode]);

  // Initialize selected variants when available dishes change
  useEffect(() => {
    const initialVariants = {};
    availableDishes.forEach((dish) => {
      if (dish.childDishes && dish.childDishes.length > 0) {
        // Select the first child dish by default
        initialVariants[dish.dish.id] = dish.childDishes[0];
      }
    });
    setSelectedVariants(initialVariants);
  }, [availableDishes]);

  const handleOnSubmit = (data) => {
    if (isCycleMode) {
      const q = (data.searchInput || "").trim().toLowerCase();
      if (!q) {
        setAvailableDishes(cycleAllDishes);
      } else {
        setAvailableDishes(
          cycleAllDishes.filter((item) => {
            const name = item.dish?.name?.[currentLanguage] || item.dish?.name?.en || "";
            return name.toLowerCase().includes(q);
          })
        );
      }
    } else if (replacementType !== "base") {
      setSubDishSearch(data.searchInput);
    } else {
      dispatch(availableDishesActions.filteredDishes(data.searchInput));
    }
  };

  const closeModal = () => {
    setIsShowReplace(false);
  };

  const handleVariantChange = (parentDishId, selectedVariant) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [parentDishId]: selectedVariant,
    }));
  };

  const handleUpdateOrderDish = async (dish) => {
    try {
      setIsLoading(true);

      let updateData;
      if (replacementType === "protein") {
        updateData = { proteinDishId: dish.dishId };
      } else if (replacementType === "sauce") {
        updateData = { sauceDishId: dish.dishId };
      } else {
        // base replacement: also replace protein/sauce if the new dish has child dishes of those types
        updateData = { dishId: dish.dishId };
        const childDishes = dish.dish?.childDishes || [];
        const proteinChild = childDishes.find(c => c.type === DishType.PROTEIN);
        const sauceChild = childDishes.find(c => c.type === DishType.SAUCE);
        if (proteinChild) updateData.proteinDishId = proteinChild.id;
        if (sauceChild) updateData.sauceDishId = sauceChild.id;
      }

      await ordersService.updateDishById(idForUpdateDishInOrderClient, updateData);

      dispatch(ordersActions.changeTrigger());
      toast.success(t("Successfully updated"));
      closeModal();
    } catch (error) {
      console.error("Error updating order dish:", error);
      toast.error(t("modal-toast-creating-error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (dish) => {
    handleUpdateOrderDish(dish);
  };

  // Loading state
  if (isFetchingDishes || status === "Loading") {
    return (
      <div className={css.overlay}>
        <div className={css.modal}>
          <div className={css.header}>
            <div className={css.title} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {t("menu-menu-title-available-dishes")}
              <span style={{
                fontSize: 11,
                fontWeight: 500,
                padding: "2px 8px",
                borderRadius: 4,
                background: isCycleMode ? "#e6f4ff" : "#f6ffed",
                color: isCycleMode ? "#1677ff" : "#52c41a",
                border: `1px solid ${isCycleMode ? "#91caff" : "#b7eb8f"}`,
              }}>
                {isCycleMode ? t("Cycle Menu") : t("Date Menu")}
              </span>
            </div>
            <div className={css.closeButton} onClick={closeModal}>
              &times;
            </div>
          </div>
          <div className={css.loadingContainer}>
            <div className={css.loader}></div>
            <p>{t("Loading available dishes...")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={css.overlay}>
      <div className={css.modal}>
        {/* Sticky Header with Current Dish Info */}
        <div className={css.stickyHeader}>
          <div className={css.header}>
            <div className={css.title} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {replacementType === "protein"
                ? t("selectProteinReplacement", { defaultValue: t("selectReplacement") + " (Protein)" })
                : replacementType === "sauce"
                ? t("selectSauceReplacement", { defaultValue: t("selectReplacement") + " (Sauce)" })
                : t("selectReplacement")}
              <span style={{
                fontSize: 11,
                fontWeight: 500,
                padding: "2px 8px",
                borderRadius: 4,
                background: isCycleMode ? "#e6f4ff" : "#f6ffed",
                color: isCycleMode ? "#1677ff" : "#52c41a",
                border: `1px solid ${isCycleMode ? "#91caff" : "#b7eb8f"}`,
              }}>
                {isCycleMode ? t("Cycle Menu") : t("Date Menu")}
              </span>
            </div>
            <div className={css.closeButton} onClick={closeModal}>
              &times;
            </div>
          </div>

          {/* Current Dish Information */}
          {currentDish && (() => {
            const displayedDish =
              replacementType === "protein"
                ? currentDish?.proteinDish
                : replacementType === "sauce"
                ? currentDish?.sauceDish
                : currentDish?.dish;
            if (!displayedDish) return null;
            return (
              <div className={css.currentDishInfo}>
                <h3 className={css.currentDishTitle}>{t("currentDish")}:</h3>
                <div className={css.currentDishContent}>
                  <div className={css.currentDishImage}>
                    <ImageReusable
                      src={displayedDish?.img}
                      alt={displayedDish?.name?.[currentLanguage] || "Current Dish"}
                      className={css.currentDishImg}
                    />
                  </div>
                  <div className={css.currentDishDetails}>
                    <p className={css.currentDishName}>
                      {displayedDish?.name?.[currentLanguage] ||
                        displayedDish?.name?.en ||
                        "Unknown Dish"}
                    </p>
                    <p className={css.currentDishStats}>
                      {displayedDish?.energy} {t("calories")}, {displayedDish?.weight} {t("grams")}
                    </p>
                    <div className={css.currentDishMacros}>
                      <div className={css.macroChip}>
                        <span className={css.macroValue}>{displayedDish?.proteins}</span>
                        <span className={css.macroLabel}>{t("modal-proteins")}</span>
                      </div>
                      <div className={css.macroChip}>
                        <span className={css.macroValue}>{displayedDish?.fats}</span>
                        <span className={css.macroLabel}>{t("modal-fats")}</span>
                      </div>
                      <div className={css.macroChip}>
                        <span className={css.macroValue}>{displayedDish?.carbs}</span>
                        <span className={css.macroLabel}>{t("modal-carbohydrates")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Main Content */}
        <div className={css.mainContent}>
          {/* Search Block */}
          <div className={css.searchBlock}>
            <form onChange={handleSubmit(handleOnSubmit)}>
              <input
                type="text"
                {...register("searchInput")}
                placeholder={t("Search dishes...")}
                className={css.searchInput}
              />
            </form>
          </div>

          {/* Available Replacements */}
          <div className={css.replacementsSection}>
            <h3 className={css.replacementsTitle}>{t("availableReplacements")}</h3>

            <div className={css.dishGrid}>
              {availableDishes.map((dish) => {
                const selectedVariant = selectedVariants[dish.dish.id] || dish;
                const displayDish = selectedVariant.dish;
                const hasVariants = dish.childDishes && dish.childDishes.length > 1;

                return (
                  <div
                    key={dish.id}
                    className={`${css.dishCard} ${
                      selectedDish === selectedVariant.id ? css.selectedCard : ""
                    }`}
                  >
                    {/* Dish Image */}
                    <div className={css.dishImageContainer}>
                      <ImageReusable
                        src={displayDish.img}
                        alt={displayDish.name?.[currentLanguage] || "Dish Image"}
                        className={css.dishImage}
                        onClick={() => {
                          setSelectedDishData(selectedVariant);
                          setIsDishDetailsOpen(true);
                        }}
                      />
                    </div>

                    {/* Dish Content */}
                    <div className={css.dishContent}>
                      <div className={css.dishNameRow}>
                        <h4
                          className={css.dishName}
                          onClick={() => {
                            setSelectedDishData(selectedVariant);
                            setIsDishDetailsOpen(true);
                          }}
                        >
                          {displayDish.name?.[currentLanguage] ||
                           displayDish.name?.en || "Unknown Dish"}
                        </h4>
                        {displayDish.type && (
                          <span className={`${css.typeBadge} ${css[`type${displayDish.type}`]}`}>
                            {displayDish.type}
                          </span>
                        )}
                      </div>

                      {/* Variant Selector */}
                      {hasVariants && (
                        <div className={css.variantSelector}>
                          <SearchableSelect
                            options={dish.childDishes?.map((variant) => ({
                              value: variant.dish.id,
                              label: `${variant.dish.energy} ${t("calories")}`,
                              dish: variant,
                            }))}
                            value={selectedVariant.dish.id}
                            onChange={(value) => {
                              const selected = dish.childDishes?.find(
                                (child) => child.dish.id === value
                              );
                              if (selected) {
                                handleVariantChange(dish.dish.id, selected);
                              }
                            }}
                            placeholder={t("Select Variant")}
                            className={css.variantDropdown}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                          />
                        </div>
                      )}

                      {/* Nutrition Info */}
                      <div className={css.nutritionInfo}>
                        <div className={css.caloriesWeight}>
                          <span className={css.calories}>
                            <strong>{displayDish.energy}</strong> {t("calories")}
                          </span>
                          <span className={css.weight}>
                            <strong>{displayDish.weight}</strong> {t("grams")}
                          </span>
                        </div>

                        <div className={css.macrosGrid}>
                          <div className={css.macroBox}>
                            <div className={css.macroValue}>{displayDish.proteins}</div>
                            <div className={css.macroLabel}>{t("modal-proteins")}</div>
                          </div>
                          <div className={css.macroBox}>
                            <div className={css.macroValue}>{displayDish.fats}</div>
                            <div className={css.macroLabel}>{t("modal-fats")}</div>
                          </div>
                          <div className={css.macroBox}>
                            <div className={css.macroValue}>{displayDish.carbs}</div>
                            <div className={css.macroLabel}>{t("modal-carbohydrates")}</div>
                          </div>
                        </div>
                      </div>

                      {/* Select Button */}
                      <button
                        onClick={() => handleSelect(selectedVariant)}
                        className={`${css.selectButton} ${
                          selectedDish === selectedVariant.id ? css.selectedButton : ""
                        } ${isLoading ? css.loadingButton : ""}`}
                        disabled={isLoading}
                      >
                        {selectedDish === selectedVariant.id
                          ? t("selected")
                          : isLoading
                          ? t("Selecting...")
                          : t("select")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {availableDishes.length === 0 && (
              <div className={css.noDishes}>
                <p>{t("No available dishes for replacement")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Dish Details Popup */}
        {selectedDishData && isDishDetailsOpen && (
          <DishDetailsPopup
            dishData={selectedDishData?.dish}
            isOpen={isDishDetailsOpen}
            setIsOpen={setIsDishDetailsOpen}
          />
        )}
      </div>
    </div>
  );
};

export { OrdersReplaceDishOrderModal };
